import React, { useCallback, useEffect, useState } from 'react';
import { useContext } from 'use-context-selector';
import classNames from 'classnames';
import { GameState } from 'contexts/gameState';
import { GenericObject } from 'types/global';
import {
  PerkTypes,
  Perks,
  PerkCategories,
  TradeoffPerk,
  UltimatePerk,
  StandardPerk,
} from 'types/perks';
import formatPerk from 'utils/formatPerk';
import move from 'utils/move';
import './perk.scss';

const banSuicideList = [
  TradeoffPerk.SPEED_DAMAGE,
  TradeoffPerk.REGEN_HEALTH,
  TradeoffPerk.RANGE_RANGE,
  StandardPerk.FREE_UPGRADE_CHANCE,
];
const banCommonList = [
  StandardPerk.INTEREST,
  StandardPerk.LAND_MINE_DAMAGE,
  StandardPerk.HEALTH_REGEN,
  StandardPerk.FREE_UPGRADE_CHANCE,
];
const banTradeoffList = [
  TradeoffPerk.SPEED_DAMAGE,
  TradeoffPerk.LIFESTEAL_KNOCKBACK,
  TradeoffPerk.REGEN_HEALTH,
  TradeoffPerk.RANGE_RANGE,
];
const defaultPriorityList = [
  TradeoffPerk.COINS_HEALTH,
  StandardPerk.PERK_WAVE_REQUIREMENT,
  UltimatePerk.GOLDEN_TOWER,
  StandardPerk.INCREASE_GAME_SPEED,
  StandardPerk.COIN_BONUSES,
  TradeoffPerk.DAMAGE_DAMAGE,
  TradeoffPerk.DAMAGE_BOSS_HEALTH,
  StandardPerk.ORBS,
  StandardPerk.DEFENSE_PERCENT,
  StandardPerk.MAX_HEALTH,
  StandardPerk.UNLOCK_ULTIMATE_WEAPON,
  UltimatePerk.CHAIN_LIGHTNING,
  UltimatePerk.CHRONO_FIELD,
  UltimatePerk.DEATH_WAVE,
  UltimatePerk.INNER_LAND_MINES,
  UltimatePerk.POISON_SWAMP,
  UltimatePerk.SMART_MISSILES,
  StandardPerk.DAMAGE,
  StandardPerk.BOUNCE_SHOT,
  StandardPerk.HEALTH_REGEN,
  StandardPerk.INTEREST,
  StandardPerk.LAND_MINE_DAMAGE,
  TradeoffPerk.LIFESTEAL_KNOCKBACK,
  TradeoffPerk.BOSS_HEALTH_SPEED,
  TradeoffPerk.CASH_WAVE_KILL,
  TradeoffPerk.HEALTH_REGEN_LIFESTEAL,
  TradeoffPerk.RANGE_RANGE,
  TradeoffPerk.SPEED_DAMAGE,
  TradeoffPerk.REGEN_HEALTH,
  StandardPerk.FREE_UPGRADE_CHANCE,
];
const defaultNeverPick = [
  TradeoffPerk.SPEED_DAMAGE,
  TradeoffPerk.REGEN_HEALTH,
  TradeoffPerk.LIFESTEAL_KNOCKBACK,
  StandardPerk.FREE_UPGRADE_CHANCE,
  TradeoffPerk.BOSS_HEALTH_SPEED,
  TradeoffPerk.CASH_WAVE_KILL,
  TradeoffPerk.RANGE_RANGE,
  TradeoffPerk.HEALTH_REGEN_LIFESTEAL,
];

const Perk: React.FC<GenericObject> = ({
  perk,
  index,
  standardResearch,
  tradeoffResearch,
  firstPerkChoice,
  bannedPerks,
  perkPriorityList,
  neverPickPerks,
  setFirstPerkChoice,
  setBannedPerks,
  setPerkPriorityList,
  setNeverPickPerks,
  isSelected,
  setSelectedPerk,
}) => {
  const moveUpPerkPriorityList = useCallback(
    e => {
      e.stopPropagation();
      if (index === 0) return;
      setSelectedPerk(index - 1);
      setPerkPriorityList(priorityList => move(priorityList, index, index - 1));
    },
    [setPerkPriorityList, setSelectedPerk, index],
  );
  const moveDownPerkPriorityList = useCallback(
    e => {
      e.stopPropagation();
      if (index >= perkPriorityList.length - 1) return;
      setSelectedPerk(index + 1);
      setPerkPriorityList(priorityList => move(priorityList, index, index + 1));
    },
    [index, perkPriorityList.length, setPerkPriorityList, setSelectedPerk],
  );
  const moveToTopOfPerkPriorityList = useCallback(
    e => {
      e.stopPropagation();
      if (index === 0) return;
      setSelectedPerk(0);
      setPerkPriorityList(priorityList => move(priorityList, index, 0));
    },
    [index, setPerkPriorityList, setSelectedPerk],
  );
  const moveToBottomOfPerkPriorityList = useCallback(
    e => {
      e.stopPropagation();
      if (index >= perkPriorityList.length - 1) return;
      setSelectedPerk(perkPriorityList.length - 1);
      setPerkPriorityList(priorityList =>
        move(priorityList, index, priorityList.length - 1),
      );
    },
    [index, perkPriorityList.length, setPerkPriorityList, setSelectedPerk],
  );
  const toggleBannedPerk = useCallback(
    ({ target: { value } }) => {
      setBannedPerks(banned => {
        if (banned.includes(value)) {
          return banned.filter(i => i !== value);
        }
        return [...banned, value];
      });
    },
    [setBannedPerks],
  );
  const toggleNeverPerk = useCallback(
    ({ target: { value } }) => {
      setNeverPickPerks(never => {
        if (never.includes(value)) {
          return never.filter(i => i !== value);
        }
        return [...never, value];
      });
    },
    [setNeverPickPerks],
  );
  const toggleFirstPerkChoice = useCallback(
    ({ target: { value } }) => {
      setFirstPerkChoice(first => (value === first ? null : value));
    },
    [setFirstPerkChoice],
  );
  const selectPerk = useCallback(
    () => setSelectedPerk(index),
    [index, setSelectedPerk],
  );
  const isFirstChoice = firstPerkChoice === perk;
  const isNeverPick = neverPickPerks.includes(perk);
  const isBanned = bannedPerks.includes(perk);
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={classNames('perk', isSelected ? 'selected' : 'unselected')}
      onClick={selectPerk}
    >
      <div
        className={classNames(
          'first-choice-label',
          isFirstChoice && 'is-first-choice',
        )}
      />
      <div
        className={classNames(
          'never-pick-label',
          isNeverPick && 'is-never-pick',
        )}
      />
      <div className={classNames('banned-label', isBanned && 'is-banned')} />
      <div className='index'>{index + 1}:</div>
      <div className='text'>
        {formatPerk(perk, standardResearch, tradeoffResearch)}
      </div>
      {isSelected && (
        <div className='perk-buttons'>
          <div className='perk-controls'>
            <button
              value={perk}
              type='button'
              className={classNames(
                'first-choice',
                isFirstChoice && 'first-choice-active',
              )}
              onClick={toggleFirstPerkChoice}
              disabled={Perks[perk].category !== PerkCategories.STANDARD}
            >
              First Choice
            </button>
            <button
              value={perk}
              type='button'
              className={classNames(
                'never-pick',
                isNeverPick && 'never-pick-active',
              )}
              onClick={toggleNeverPerk}
            >
              Never Pick
            </button>
            <button
              value={perk}
              type='button'
              className={classNames('banned', isBanned && 'banned-active')}
              onClick={toggleBannedPerk}
            >
              Ban Perk
            </button>
          </div>
          <div className='movement'>
            <button
              value={perk}
              type='button'
              className='move-perk'
              onClick={moveToTopOfPerkPriorityList}
              disabled={index === 0}
            >
              Top
            </button>
            <button
              value={perk}
              type='button'
              className='move-perk'
              onClick={moveUpPerkPriorityList}
              disabled={index === 0}
            >
              Up
            </button>
            <button
              value={perk}
              type='button'
              className='move-perk'
              onClick={moveDownPerkPriorityList}
              disabled={index === perkPriorityList.length - 1}
            >
              Down
            </button>
            <button
              value={perk}
              type='button'
              className='move-perk'
              onClick={moveToBottomOfPerkPriorityList}
              disabled={index === perkPriorityList.length - 1}
            >
              Bottom
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const PerkList: React.FC<{}> = () => {
  const {
    standardResearch,
    tradeoffResearch,

    firstPerkChoice,
    bannedPerks,
    perkPriorityList,
    neverPickPerks,

    setFirstPerkChoice,
    setBannedPerks,
    setPerkPriorityList,
    setNeverPickPerks,
  } = useContext(GameState);
  const [selectedPerk, setSelectedPerk] = useState(0);

  const banTradeoffs = useCallback(() => {
    setPerkPriorityList(defaultPriorityList);
    setNeverPickPerks(defaultNeverPick);
    setBannedPerks(banTradeoffList);
  }, [setBannedPerks, setNeverPickPerks, setPerkPriorityList]);
  const banSuicides = useCallback(() => {
    setPerkPriorityList(defaultPriorityList);
    setNeverPickPerks(defaultNeverPick);
    setBannedPerks(banSuicideList);
  }, [setBannedPerks, setNeverPickPerks, setPerkPriorityList]);
  const banCommons = useCallback(() => {
    setPerkPriorityList(defaultPriorityList);
    setNeverPickPerks(defaultNeverPick);
    setBannedPerks(banCommonList);
  }, [setBannedPerks, setNeverPickPerks, setPerkPriorityList]);

  useEffect(() => {
    const missingPerks: PerkTypes[] = Object.keys(Perks).filter(
      perk => !perkPriorityList.includes(perk as PerkTypes),
    ) as PerkTypes[];
    if (missingPerks.length > 0) {
      setPerkPriorityList([...perkPriorityList, ...missingPerks]);
    }
  }, [perkPriorityList, setPerkPriorityList]);
  return (
    <div id='perk-list'>
      <div className='header'>
        <div className='title'>
          Perk Priority List (click a perk to select it)
        </div>
        <div className='presets'>
          <button type='button' onClick={banSuicides}>
            Ban Suicide Preset
          </button>
          <button type='button' onClick={banCommons}>
            Ban Common Preset
          </button>
          <button type='button' onClick={banTradeoffs}>
            Ban Tradeoff Preset
          </button>
        </div>
      </div>
      {perkPriorityList.map((perk, i) => (
        <Perk
          key={perk}
          perk={perk}
          index={i}
          isSelected={selectedPerk === i}
          setSelectedPerk={setSelectedPerk}
          standardResearch={standardResearch}
          tradeoffResearch={tradeoffResearch}
          firstPerkChoice={firstPerkChoice}
          bannedPerks={bannedPerks}
          perkPriorityList={perkPriorityList}
          neverPickPerks={neverPickPerks}
          setFirstPerkChoice={setFirstPerkChoice}
          setBannedPerks={setBannedPerks}
          setPerkPriorityList={setPerkPriorityList}
          setNeverPickPerks={setNeverPickPerks}
        />
      ))}
    </div>
  );
};

export default PerkList;
